import { Link } from "react-router-dom";
import { ScanFace, MapPin, Coffee, FileBarChart, BellRing, Building2, LogIn, CheckCircle2, ArrowRight, ShieldCheck } from "lucide-react";
import SiteFooter from "@/components/SiteFooter";
import { StoreButtons, useAppLinks } from "@/components/StoreButtons";

const FEATURES = [
  { icon: ScanFace, title: "AI face check-in", text: "Staff clock in with a selfie. AI matches it to their profile in seconds, so nobody clocks in for a friend." },
  { icon: MapPin, title: "Geofenced sites", text: "Draw a radius around every site. Check-ins only count when staff are actually on location." },
  { icon: Coffee, title: "Desk sessions", text: "Track every Leave Desk and Return to Desk with category, duration and outcome." },
  { icon: FileBarChart, title: "Ready-made reports", text: "Daily, weekly and monthly attendance, late arrivals and hours worked, exported in one tap." },
  { icon: BellRing, title: "Instant alerts", text: "Managers get email and push alerts the moment someone is late or checks in." },
  { icon: Building2, title: "Multi-company", text: "Run several businesses and sites from one portal, with each team's data kept separate." },
];

function NavBar() {
  return (
    <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b border-slate-100">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
          <img src="/logo-lg.webp" alt="Smart-In" className="h-9 w-9 rounded-lg object-cover shadow-sm" />
          <span className="font-head font-extrabold text-lg text-slate-900">Smart-In</span>
        </Link>
        <nav className="flex items-center gap-2 sm:gap-6">
          <a href="#features" data-testid="landing-nav-features" className="hidden sm:inline text-sm font-semibold text-slate-600 hover:text-[#FF5A4D] transition-colors">Features</a>
          <Link to="/support" data-testid="landing-nav-support" className="hidden sm:inline text-sm font-semibold text-slate-600 hover:text-[#FF5A4D] transition-colors">Support</Link>
          <Link to="/login" data-testid="landing-login-btn"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-[#FFC400] hover:bg-[#EAB000] text-[#1A1500] text-sm font-bold transition-colors">
            <LogIn className="h-4 w-4" /> Login
          </Link>
        </nav>
      </div>
    </header>
  );
}

function PhoneMockup() {
  return (
    <div className="relative mx-auto w-[260px] sm:w-[290px]" data-testid="landing-phone-mockup">
      <div className="absolute -inset-10 rounded-full bg-[#FFC400]/30 blur-3xl" />
      <div className="relative rounded-[2.75rem] bg-slate-900 p-3 shadow-2xl shadow-slate-900/30 rotate-[-4deg]">
        <div className="rounded-[2.2rem] bg-white overflow-hidden">
          <div className="h-7 flex justify-center pt-2"><div className="h-4 w-24 rounded-full bg-slate-900" /></div>
          <div className="px-5 pb-6 pt-3">
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Good morning</p>
            <p className="font-head text-lg font-extrabold text-slate-900">Aisha Rahman</p>
            <div className="mt-4 rounded-2xl bg-[#FFF4CC] p-4">
              <div className="mx-auto h-28 w-28 rounded-full border-4 border-dashed border-[#FF5A4D] grid place-items-center">
                <ScanFace className="h-12 w-12 text-[#1A1500]" />
              </div>
              <p className="mt-3 text-center text-xs font-semibold text-slate-600">Look at the camera</p>
            </div>
            <div className="mt-3 flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2.5">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <span className="text-xs font-bold text-emerald-700">Face matched · 98%</span>
            </div>
            <div className="mt-2 flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2.5">
              <MapPin className="h-4 w-4 text-[#FF5A4D]" />
              <span className="text-xs font-semibold text-slate-600">Head Office · inside zone</span>
            </div>
            <div className="mt-4 h-12 rounded-xl bg-[#FFC400] grid place-items-center text-sm font-extrabold text-[#1A1500]">Clock In · 08:57</div>
          </div>
        </div>
      </div>
      <div className="absolute -left-6 sm:-left-14 bottom-16 rounded-2xl bg-white shadow-xl px-4 py-3 flex items-center gap-2.5 rotate-[3deg]">
        <BellRing className="h-5 w-5 text-[#FF5A4D]" />
        <div className="leading-tight">
          <p className="text-xs font-bold text-slate-900">On time</p>
          <p className="text-[10px] text-slate-500">Manager notified</p>
        </div>
      </div>
    </div>
  );
}

function Hero({ links }) {
  return (
    <section className="relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-12 pb-20 sm:pt-20 sm:pb-28 grid lg:grid-cols-[1.15fr_1fr] gap-14 lg:gap-8 items-center">
        <div className="animate-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#FFEDEB] px-3.5 py-1.5 text-xs font-bold text-[#FF5A4D]">
            <ShieldCheck className="h-3.5 w-3.5" /> AI-powered attendance
          </span>
          <h1 className="mt-5 font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]" data-testid="landing-hero-title">
            Attendance your team <span className="relative whitespace-nowrap"><span className="relative z-10">can't fake</span><span className="absolute left-0 right-0 bottom-1 h-3 sm:h-4 bg-[#FFC400] -z-0 rounded" /></span>.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-xl">
            Smart-In verifies every clock-in with face recognition and location, then gives managers live dashboards, alerts and reports — all from one simple app.
          </p>
          <div className="mt-8"><StoreButtons links={links} prefix="hero" /></div>
          <Link to="/login" data-testid="landing-hero-login-link"
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-slate-900 hover:text-[#FF5A4D] transition-colors">
            Admin? Sign in to the portal <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <PhoneMockup />
      </div>
    </section>
  );
}

function Features() {
  return (
    <section id="features" className="bg-slate-50 border-y border-slate-100" data-testid="landing-features">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A4D]">Features</p>
        <h2 className="mt-3 font-head text-3xl sm:text-4xl font-extrabold text-slate-900 max-w-2xl">Everything you need to run attendance with confidence</h2>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map(({ icon: Icon, title, text }, i) => (
            <div key={title} data-testid={`landing-feature-${i}`}
              className="group rounded-2xl bg-white border border-slate-200 p-6 transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-lg">
              <div className="grid place-items-center h-12 w-12 rounded-xl bg-[#FFF4CC] text-[#1A1500] transition-colors group-hover:bg-[#FFC400]">
                <Icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 font-head text-lg font-bold text-slate-900">{title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaBand({ links }) {
  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
      <div className="rounded-3xl bg-[#FFC400] px-6 py-12 sm:px-12 flex flex-col lg:flex-row gap-8 lg:items-center lg:justify-between" data-testid="landing-cta">
        <div>
          <h2 className="font-head text-2xl sm:text-3xl font-extrabold text-[#1A1500]">Get the Smart-In app</h2>
          <p className="mt-2 text-[#1A1500]/75 max-w-md">Staff download the app, managers sign in to the portal. Set up your first site in minutes.</p>
        </div>
        <StoreButtons links={links} prefix="cta" />
      </div>
    </section>
  );
}

export default function Landing() {
  const links = useAppLinks();

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="landing-page">
      <NavBar />
      <main className="flex-1">
        <Hero links={links} />
        <Features />
        <CtaBand links={links} />
      </main>
      <SiteFooter />
    </div>
  );
}
